import http from "./httpService";
import auth from "./authService";
import { Stockpile } from "../interface/api/Stockpile";
import { ContainmentPad } from "../interface/api/ContainmentPad";

http.setJwt(auth.getJwt());

export const getStockpiles = () => {
  return http.get("/api/storagearea/stockpile/all");
};

export const getStockpile = (id: number | string) => {
  return http.get(`/api/storagearea/stockpile/${id}`);
};

export const deleteStockpile = (id: number | string) => {
  return http.delete(`/api/storagearea/stockpile/${id}`);
};

export function saveStockpile(stockpile: Stockpile) {
  if (stockpile && stockpile.id > 0) {
    return http.put("/api/storagearea/stockpile/edit", stockpile);
  }
  return http.post("/api/storagearea/stockpile/new", stockpile);
}

// ----- Stockpile workflow -----

export const getActiveStockpiles = () => {
  return http.get("/api/storagearea/stockpile/active");
};

export const closeStockpileForSampling = (id: number | string) => {
  return http.post(`/api/storagearea/stockpile/${id}/close-for-sampling`, {});
};

export const getStockpileLoads = (id: number | string) => {
  return http.get(`/api/storagearea/stockpile/${id}/loads`);
};

export const setStockpileSamples = (
  id: number | string,
  ticketIds: number[]
) => {
  return http.post(`/api/storagearea/stockpile/${id}/samples`, { ticketIds });
};

export const uploadStockpileLab = (id: number | string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return http.post(`/api/storagearea/stockpile/${id}/lab-result`, form);
};

export const removeStockpileLab = (id: number | string) => {
  return http.delete(`/api/storagearea/stockpile/${id}/lab-result`);
};

export const approveStockpile = (
  id: number | string,
  sampleDate: string,
  signature: string
) => {
  return http.post(`/api/storagearea/stockpile/${id}/approve`, {
    sampleDate,
    signature,
  });
};

export const getStockpileSample = (id: number | string) => {
  return http.get(`/api/storagearea/stockpile/${id}/sample`);
};

export const getContainmentPads = () => {
  return http.get("/api/storagearea/containmentpad/all");
};

export const getContainmentPad = (id: number | string) => {
  return http.get(`/api/storagearea/containmentpad/${id}`);
};

export const deleteContainmentPad = (id: number | string) => {
  return http.delete(`/api/storagearea/containmentpad/${id}`);
};

export function saveContainmentPad(containmentPad: ContainmentPad) {
  if (containmentPad && containmentPad.id > 0) {
    return http.put("/api/storagearea/containmentpad/edit", containmentPad);
  }
  return http.post("/api/storagearea/containmentpad/new", containmentPad);
}

// ----- Containment pad workflow -----

export const getActiveContainmentPads = () => {
  return http.get("/api/storagearea/containmentpad/active");
};

export const closeContainmentPadForSampling = (id: number | string) => {
  return http.post(
    `/api/storagearea/containmentpad/${id}/close-for-sampling`,
    {}
  );
};

export const getContainmentPadLoads = (id: number | string) => {
  return http.get(`/api/storagearea/containmentpad/${id}/loads`);
};

export const uploadContainmentPadLab = (id: number | string, file: File) => {
  const form = new FormData();
  form.append("file", file);
  return http.post(`/api/storagearea/containmentpad/${id}/lab-result`, form);
};

export const removeContainmentPadLab = (id: number | string) => {
  return http.delete(`/api/storagearea/containmentpad/${id}/lab-result`);
};

export const approveContainmentPad = (
  id: number | string,
  sampleDate: string,
  sampleReferenceNumber: string,
  signature: string
) => {
  return http.post(`/api/storagearea/containmentpad/${id}/approve`, {
    sampleDate,
    sampleReferenceNumber,
    signature,
  });
};

export const getContainmentPadSample = (id: number | string) => {
  return http.get(`/api/storagearea/containmentpad/${id}/sample`);
};

// ----- Summary reports -----

// The Small Quantities / Hydrovac summary reports: every stockpile or pad closed with a lab
// report between the two dates, emailed as Excel to the given emails (the logged-in user when
// omitted). 204 means nothing closed in the window.
const summaryReportUrl = (
  area: "stockpile" | "containmentpad",
  fromDate: Date,
  toDate: Date,
  emails?: string[]
) => {
  const params = [
    `fromDate=${encodeURIComponent(fromDate.toISOString())}`,
    `toDate=${encodeURIComponent(toDate.toISOString())}`,
    ...(emails ?? []).map((email) => `emails=${encodeURIComponent(email)}`),
  ];
  return `/api/storagearea/${area}/report?${params.join("&")}`;
};

export const sendStockpileSummaryReport = (
  fromDate: Date,
  toDate: Date,
  emails?: string[]
) => {
  return http.get(summaryReportUrl("stockpile", fromDate, toDate, emails));
};

export const sendContainmentPadSummaryReport = (
  fromDate: Date,
  toDate: Date,
  emails?: string[]
) => {
  return http.get(summaryReportUrl("containmentpad", fromDate, toDate, emails));
};

export default {
  getStockpiles,
  getStockpile,
  deleteStockpile,
  saveStockpile,
  getActiveStockpiles,
  closeStockpileForSampling,
  getStockpileLoads,
  setStockpileSamples,
  uploadStockpileLab,
  removeStockpileLab,
  approveStockpile,
  getStockpileSample,
  getContainmentPads,
  getContainmentPad,
  deleteContainmentPad,
  saveContainmentPad,
  getActiveContainmentPads,
  closeContainmentPadForSampling,
  getContainmentPadLoads,
  uploadContainmentPadLab,
  removeContainmentPadLab,
  approveContainmentPad,
  getContainmentPadSample,
  sendStockpileSummaryReport,
  sendContainmentPadSummaryReport,
};
